import React, {useEffect} from "react"
import {useFormik} from "formik";
import * as yup from "yup";

import PasswordField from "../../../../components/PasswordField";
import {useFeedbackTools} from "../../../../components/FeedbackTools";

import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField/TextField";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import FormControlLabel from "@mui/material/FormControlLabel";
import RadioGroup from "@mui/material/RadioGroup";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Checkbox from "@mui/material/Checkbox";
import FormGroup from "@mui/material/FormGroup";
import Radio from "@mui/material/Radio";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';


const portOptions = [25, 465, 587];

export const useSmtpFormik = (emailFrom, smtpOptions, onSubmit) => {
    const [, domain] = emailFrom.split("@");
    const {showErrorNotification} = useFeedbackTools();

    // Default option is tls, but it will only be used if port is 465, otherwise in case of port 587
    // is ignored because of starttls.
    let sslProtocol = "tls";
    if(smtpOptions.value?.ssl === true){
        sslProtocol = "ssl";
    }

    return useFormik({
        initialValues: {
            userName: smtpOptions.value?.userName || "",
            password: smtpOptions.value?.password || "",
            address: smtpOptions.value?.address || "",
            domain: smtpOptions.value?.domain || domain,
            port: smtpOptions.value?.port || 587,
            authentication: smtpOptions.value?.authentication || "login",
            disableCertificateVerification: smtpOptions.value?.opensslVerifyMode === "none",
            sslProtocol: sslProtocol  // This ssl protocol state is used for the radio group.
        },
        validationSchema: yup.object().shape({
            address: yup.string().required('Campo obbligatorio'),
            userName: yup.string().required('Campo obbligatorio'),
            password: yup.string().required('Campo obbligatorio'),
            domain: yup.string().required('Campo obbligatorio'),
            port: yup.number().required('Campo obbligatorio'),
            authentication: yup.string().required('Campo obbligatorio'),
        }),
        onSubmit: async (values) => {
            const {sslProtocol, disableCertificateVerification, ...options} = values;  // remove ssl protocol from the data to send to the server

            // Enable direct tls or ssl when port is 465, do not force tls or ssl when port is 587 (starttls) or 25
            if(values.port.toString() === "465"){
                if(sslProtocol === "tls"){
                    options["tls"] = true;
                    options["ssl"] = false;
                }else if(sslProtocol === "ssl") {
                    options["tls"] = false;
                    options["ssl"] = true;
                }
            }else{
                options["tls"] = false;
                options["ssl"] = false;
            }

            // Set the opensslVerifyMode
            if(disableCertificateVerification === true){
                options["opensslVerifyMode"] = "none"
            } else {
                options["opensslVerifyMode"] = "peer"
            }

            try{
                await smtpOptions.update(options);
                await onSubmit();
            }catch(e){
                showErrorNotification(e);
            }
        }
    });
}

const StepSmtp = (props) => {
    const {formik, emailFrom} = props;

    useEffect(
        () => {
            if (formik.values.port.toString() === "25") {
                formik.setFieldValue("sslProtocol", "nothing");
            } else if(formik.values.port.toString() === "587") {
                formik.setFieldValue("sslProtocol", "tls");
            }
        },
        [formik.values.port]  // eslint-disable-line react-hooks/exhaustive-deps
    )

    return <Grid container spacing={2}>
        <Grid item xs={12}>
            <Typography variant="body2">
                Configura le seguenti impostazioni SMTP per inviare le mail da <strong>{emailFrom}</strong>
            </Typography>
        </Grid>

        <Grid item xs={12}>
            <TextField label="Dominio"
                       size="small"
                       fullWidth
                       name="domain"
                       required
                       value={formik.values.domain}
                       onChange={formik.handleChange}
                       error={formik.touched.domain && Boolean(formik.errors.domain)}
                       helperText={formik.touched.domain && formik.errors.domain}/>
        </Grid>
        <Grid item xs={12} md={10}>
            <TextField label="Indirizzo server SMTP"
                       size="small"
                       fullWidth
                       name="address"
                       required
                       value={formik.values.address}
                       onChange={formik.handleChange}
                       error={formik.touched.address && Boolean(formik.errors.address)}
                       helperText={formik.touched.address && formik.errors.address}/>
        </Grid>
        <Grid item xs={12} md={2}>
            <TextField label="Porta"
                       select
                       fullWidth
                       size="small"
                       name="port"
                       required
                       value={formik.values.port}
                       onChange={formik.handleChange}
                       error={formik.touched.port && Boolean(formik.errors.port)}
                       helperText={formik.touched.port && formik.errors.port}>
                {
                    portOptions.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)
                }
            </TextField>
        </Grid>
        <Grid item xs={12}>
            <TextField label="Nome utente"
                       size="small"
                       fullWidth
                       name="userName"
                       required
                       value={formik.values.userName}
                       onChange={formik.handleChange}
                       error={formik.touched.userName && Boolean(formik.errors.userName)}
                       helperText={formik.touched.userName && formik.errors.userName}/>
        </Grid>
        <Grid item xs={12}>
            <PasswordField label="Password"
                           size="small"
                           fullWidth
                           name="password"
                           required
                           value={formik.values.password}
                           onChange={formik.handleChange}
                           error={formik.touched.password && Boolean(formik.errors.password)}
                           helperText={formik.touched.password && formik.errors.password}/>
        </Grid>
        <Grid item xs={12} md={12}>
            <FormControl>
                <FormLabel id="connection-radio-buttons-group-label">Tipo connessione</FormLabel>
                <RadioGroup
                    aria-labelledby="connection-radio-buttons-group-label"
                    name="sslProtocol"
                    value={formik.values.sslProtocol}
                    onChange={formik.handleChange}>
                    <FormControlLabel value="tls" control={<Radio size="small"/>} label="TLS" disabled={formik.values.port.toString() === "25"}/>
                    <FormControlLabel value="ssl" control={<Radio size="small"/>} label="SSL" disabled={formik.values.port.toString() === "25"}/>
                    <FormControlLabel value="nothing" control={<Radio size="small"/>} label="Connessione non protetta (sconsigliato)" disabled={formik.values.port.toString() !== "25"}/>
                </RadioGroup>
            </FormControl>
        </Grid>
        <Grid item xs={12}>
            <Accordion elevation={0} disableGutters>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                    sx={{p:0}}
                >
                    <Typography>Configurazioni avanzate</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{p:0}}>
                    <FormGroup>
                        <FormControlLabel control={<Checkbox name="disableCertificateVerification"
                                                             checked={formik.values.disableCertificateVerification}
                                                             onChange={formik.handleChange}
                                                             inputProps={{ 'aria-label': 'controlled' }}/>}
                                          label="Disabilita la verifica del certificato (sconsigliato)" />
                    </FormGroup>
                </AccordionDetails>
            </Accordion>
        </Grid>
    </Grid>
}

export default StepSmtp;